import type { FC, RefObject } from "react";
import { StressPattern } from "@/components/stress-pattern";
import { ResultBadge } from "@/components/ui/result-badge";
import { usePlayablePlaybackState } from "@/lib/audio/use-play-sound";
import { cn } from "@/lib/cn";
import { PLAYBACK_WARMUP_MS } from "@/lib/constants";
import type { AudioFile, OMO } from "@/lib/data/words";

export type DisplayItem = {
  audio: AudioFile;
  isOddOne: boolean;
  stressIndex: number;
};

export const buildDisplayItems = (wordData: (typeof OMO)[number]) => {
  const oddItem: DisplayItem = {
    audio: wordData.oddOneAudio,
    isOddOne: true,
    stressIndex: wordData.oddOneStressIndex,
  };

  const matchingItems: DisplayItem[] = wordData.matchingAudios.map((audio) => ({
    audio,
    isOddOne: false,
    stressIndex: wordData.matchingStressIndex,
  }));

  return [
    ...matchingItems.slice(0, wordData.oddOnePosition),
    oddItem,
    ...matchingItems.slice(wordData.oddOnePosition),
  ];
};

export const WordOptions: FC<{
  displayItems: DisplayItem[];
  selected: number | null;
  oddOnePosition: number;
  syllableCount: number;
  highlightOdd?: boolean;
  highlightAll?: boolean;
  oddItemRef?: RefObject<HTMLDivElement | null>;
  onSelect?: (index: number) => void;
}> = (props) => {
  const answered = props.selected !== null;
  return (
    <div className="flex w-full items-start justify-evenly">
      {props.displayItems.map((item, index) => (
        <WordOption
          key={item.audio.baseFileName}
          answered={answered}
          highlight={
            !answered &&
            (props.highlightAll ||
              (props.highlightOdd && index === props.oddOnePosition))
          }
          isOdd={index === props.oddOnePosition}
          isSelected={props.selected === index}
          item={item}
          onSelect={props.onSelect ? () => props.onSelect?.(index) : undefined}
          position={index + 1}
          ref={index === props.oddOnePosition ? props.oddItemRef : null}
          syllableCount={props.syllableCount}
        />
      ))}
    </div>
  );
};

const WordOption: FC<{
  answered: boolean;
  highlight?: boolean;
  isOdd: boolean;
  isSelected: boolean;
  item: DisplayItem;
  onSelect?: () => void;
  position: number;
  ref?: RefObject<HTMLDivElement | null> | null;
  syllableCount: number;
}> = ({
  answered,
  highlight,
  isOdd,
  isSelected,
  item,
  onSelect,
  position,
  ref,
  syllableCount,
}) => {
  const playback = usePlayablePlaybackState({
    kind: "audioAsset",
    data: item.audio.baseFileName,
  });
  const showCorrect = answered && isOdd;
  const showWrong = answered && isSelected && !isOdd;
  const content = (
    <>
      <div className="flex h-10 w-10 items-center justify-center">
        {answered ? (
          <StressPattern
            syllableCount={syllableCount}
            stressIndex={item.stressIndex}
          />
        ) : (
          <span className="font-bold text-2xl leading-none">{position}</span>
        )}
      </div>
      <ResultBadge
        result={showCorrect ? "correct" : showWrong ? "wrong" : "hidden"}
      />
    </>
  );
  const cardClassName = cn(
    "omo-word-option flex h-24 w-24 flex-col items-center justify-center gap-2 rounded-lg border-2 border-transparent bg-background p-3 transition-all duration-300 motion-reduce:transition-none",
    !answered && onSelect && "hover:border-foreground",
    playback.isPlaying && "omo-word-option-playing",
    showCorrect && "omo-word-option-correct",
    showWrong && "omo-word-option-wrong",
    highlight &&
      "omo-word-option-highlight text-foreground ring-1 ring-foreground/35",
  );
  const cardStyle = {
    ["--omo-playback-duration" as string]: `${item.audio.durationMs + (playback.hasWarmup ? PLAYBACK_WARMUP_MS : 0)}ms`,
  };

  return (
    <div ref={ref} className="flex w-24 flex-col gap-2">
      {answered ? (
        <div className={cardClassName} style={cardStyle}>
          {content}
        </div>
      ) : (
        <button
          type="button"
          disabled={!onSelect}
          className={cardClassName}
          style={cardStyle}
          onClick={onSelect}
        >
          {content}
        </button>
      )}
    </div>
  );
};
